/**
 * The sagas building a request's merged document: the combine itself, its
 * version and audit bookkeeping, and the set's Bates settings.
 */
import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
  takeLeading,
} from 'typed-redux-saga/macro';
import { enqueueSnackbar } from '@common-modules/snackbar';
import { ensureSetUnlocked } from './setLockGate';
import { sagaIntl } from '@i18n';

import { selectToken, selectGraphQLConfig } from '../documents/sagas/auth';
import { serviceDeleteAsset } from '../documents/services';
import {
  CombinedDocumentDetails,
  serviceFetchPdfBytes,
  serviceLatestCombinedAsset,
  serviceNextCombinedVersion,
  serviceResolveMemberOutputs,
  serviceSaveCombinedDocumentAsset,
  serviceUpdateLatestCombinedVersion,
  serviceListCombinedAssetIds,
  serviceCombinedAssetDetails,
  serviceUpdateCombinedAssetDetails,
  serviceRenameProjectTdo,
} from './combineServices';
import type { ProjectBatesStamp } from './batesStamp';
import {
  SET_PROJECT_BATES_REQUEST,
  CLEAR_PROJECT_BATES_REQUEST,
  CLEAR_PROJECT_BATES_SUCCESS,
  SET_PROJECT_BATES_SUCCESS,
  SET_PROJECT_BATES_FAILURE,
  COMBINE_PROJECT_FAILURE,
  COMBINE_PROJECT_REQUEST,
  COMBINE_PROJECT_SUCCESS,
  LOAD_COMBINED_FAILURE,
  LOAD_COMBINED_REQUEST,
  DELETE_COMBINED_REQUEST,
  DELETE_COMBINED_SUCCESS,
  DELETE_COMBINED_FAILURE,
  COMBINE_PROJECT_VERSION,
  SET_COMBINED_PREVIEW,
  INCREMENT_COMBINED_DOWNLOAD,
  RENAME_PROJECT_REQUEST,
  RENAME_PROJECT_FAILURE,
  RENAME_PROJECT_SUCCESS,
  LOAD_COMBINED_SUCCESS,
  LOAD_PROJECT_REDACTION_SUCCESS,
} from './actions';
import { SET_TAB } from '../documents/actions';
import { clampBatesSettings } from '../documents/batesLabel';
import { fetchFileOutputs } from '@redact-modules/casemedia/services';
import { isSameBatesSettings } from './batesStamp';
import type { BatesStampSettings } from '../documents/models';
import type { TDOId } from '@common-modules/universal/models/Brands';
import {
  EMPTY_PROJECT_REDACTION_DATA,
  type ProjectRedactionData,
} from './projectRedactionAsset';
import { serviceWriteProjectRedactionData } from './projectRedactionServices';
import {
  selectProjectRedaction,
  selectPreviewingCombinedFor,
  selectProjectBatesStamp,
  selectRenamedFrom,
} from './selectors';
import {
  clip,
  emitProjectAuditEvent,
  describeMemberIds,
  MERGED_DOCUMENT_SOURCE_NAME,
} from './auditEvents';
import { auditUserName } from '@helpers/emitTdoAuditEvent';
import type { SourcedAuditEventRecord } from '@common/utils/auditLogCsv';
import { combinePdfs } from '../documents/burnInWorkerClient';
import { buildMergedDocumentFilename } from '../documents/filename';
import { assertsNothingToRedact } from '../documents/helpers';
import {
  buildMergedAuditLogCsv,
  serviceListMergedAuditLogAssets,
  serviceSaveMergedAuditLogAsset,
} from './mergedAuditLog';

function* handleCombineProject(
  action: ReturnType<typeof COMBINE_PROJECT_REQUEST>
) {
  /**
   * How many parts to download at once. Three is a guess with a reason: enough
   * to overlap latency on a typical request, few enough that a twenty-file merge
   * does not open twenty connections to the asset host at once.
   */
  const PART_FETCH_CONCURRENCY = 3;

  const {
    projectTdoId,
    requestName,
    members,
    excludable = [],
  } = action.payload;

  /**
   * THE TERMINAL ACTION FIRST, then the sentence.
   *
   * A caller can be WAITING on this: a Redact All run hands off to the
   * combine, sets itself to `merging` and stops reporting, so the only
   * thing that ends it is one of this saga's two terminal actions. Putting
   * the snackbar first made the reviewer's message a prerequisite for the
   * state change, which is the wrong way round when the state change is
   * what clears a spinner.
   */
  const fail = function* (message: string) {
    yield* put(COMBINE_PROJECT_FAILURE({ projectTdoId }));
    yield* put(enqueueSnackbar({ message, variant: 'error' }));
  };

  /**
   * EVERYTHING IS INSIDE THE TRY, including the preamble.
   *
   * `sagaIntl()`, the two selects and the two-member check used to sit
   * outside it. A throw from any of them escaped to `keepAlive`, which
   * reports to Sentry and restarts the watcher — without dispatching
   * `COMBINE_PROJECT_FAILURE`. Nobody noticed while the only consequence
   * was a missing snackbar; now a Redact All run waits on that action, and
   * its absence pins the run at `merging` for the life of the tab: spinner
   * forever, Dismiss inert, every Update disabled behind
   * `redactAllInFlight`.
   */
  try {
    const intl = sagaIntl();
    const token: string = yield* select(selectToken);
    const cfg = yield* select(selectGraphQLConfig);

    if (members.length < 2) {
      yield* fail(
        intl.formatMessage({
          id: 'mergedDocumentNeedsTwo',
          defaultMessage: 'Select at least two documents.',
        })
      );
      return;
    }

    // VE-31538. A rebuild reads every member and replaces the merged output,
    // so running one while a colleague is mid-edit in a member produces a
    // document that is already out of date and discards nothing visibly.
    // The gate has already told them why; this only has to end the run, and
    // ending it matters — a Redact All handoff waits on a terminal action.
    if (!(yield* ensureSetUnlocked(projectTdoId))) {
      yield* put(COMBINE_PROJECT_FAILURE({ projectTdoId }));
      return;
    }

    const { outputs, missing, workedOnWithoutOutput } = yield* call(
      serviceResolveMemberOutputs,
      members,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    /**
     * A FILE NOBODY HAS OPENED IS LEFT OUT, not a reason to refuse.
     */
    const excludableByTdo = new Map(
      excludable.map((entry) => [String(entry.tdoId), entry.name])
    );
    /**
     * AND THE CALLER'S LIST IS CHECKED, not taken on trust.
     */
    for (const tdoId of workedOnWithoutOutput) {
      excludableByTdo.delete(String(tdoId));
    }
    const excludedMembers = missing.filter((tdoId) =>
      excludableByTdo.has(String(tdoId))
    );
    const blockingMissing = missing.filter(
      (tdoId) => !excludableByTdo.has(String(tdoId))
    );
    if (blockingMissing.length > 0) {
      // Named by count rather than by filename: the panel is where the
      // reviewer can see WHICH, and a snackbar listing five names is a
      // paragraph nobody reads.
      yield* fail(
        intl.formatMessage(
          {
            id: 'mergedDocumentMembersNotRedacted',
            defaultMessage:
              '{count, plural, one {# file has} other {# files have}} not been redacted yet.',
          },
          { count: blockingMissing.length }
        )
      );
      return;
    }

    // In the order the saga was given, which is the project's member order and
    // therefore the released document's page order. `serviceResolveMemberOutputs`
    // preserves it; this re-sorts anyway so the guarantee is local and the
    // ordering cannot be lost by a change over there.
    /**
     * AND CHECKED THE OTHER WAY, where the set is numbered (VE-32395).
     *
     * A member the caller listed as left out spans no Bates numbers, so the
     * members after it were burned starting where it begins. If it has an
     * output after all — worked on since the caller's rows were read — it
     * would be merged in carrying numbers its neighbours already drew. Refuse,
     * and re-read those rows so the next press sees it as the member it now
     * is — nothing else on the editor route would, and without it the next
     * press is refused for the same reason.
     */
    // An UNREAD record skips the check, and that is safe rather than lax: no
    // member can have been burned against numbering nobody has read — the
    // burn's governance is `unresolved` then, and draws nothing.
    const numbered = !!(yield* select(selectProjectBatesStamp(projectTdoId)));
    const workedOnSinceListed = outputs.filter((output) =>
      excludableByTdo.has(String(output.tdoId))
    );
    if (numbered && workedOnSinceListed.length > 0) {
      yield* fail(
        intl.formatMessage(
          {
            id: 'mergedDocumentExcludedMemberWorkedOn',
            defaultMessage:
              '{count, plural, one {# file that was going to be left out has} other {# files that were going to be left out have}} since been redacted. Update the merged document again.',
          },
          { count: workedOnSinceListed.length }
        )
      );
      const [first, ...rest] = workedOnSinceListed.map((o) => o.tdoId);
      if (first) {
        yield* put(fetchFileOutputs([first, ...rest]));
      }
      return;
    }

    const byTdo = new Map(outputs.map((o) => [o.tdoId, o]));
    const ordered = members
      .map((tdoId) => byTdo.get(tdoId))
      .filter((o): o is NonNullable<typeof o> => !!o);

    // AND THE MINIMUM IS CHECKED AGAIN, AFTER THE EXCLUSIONS.
    if (ordered.length < 2) {
      yield* fail(
        intl.formatMessage({
          id: 'mergedDocumentTooFewAfterExclusions',
          defaultMessage:
            'Only one of the selected documents has been redacted, so there is nothing to merge yet.',
        })
      );
      return;
    }

    if (ordered.some((output) => !output.signedUri)) {
      yield* fail(
        intl.formatMessage({
          id: 'mergedDocumentPartUnavailable',
          defaultMessage: 'A redacted file could not be read.',
        })
      );
      return;
    }
    /**
     * A FEW AT A TIME, not one after another.
     */
    const parts: ArrayBuffer[] = new Array<ArrayBuffer>(ordered.length);
    for (
      let start = 0;
      start < ordered.length;
      start += PART_FETCH_CONCURRENCY
    ) {
      const slice = ordered.slice(start, start + PART_FETCH_CONCURRENCY);
      const fetched = yield* all(
        slice.map((output) => call(serviceFetchPdfBytes, output.signedUri!))
      );
      // Written back BY INDEX, so the page order survives the batching: the
      // released document's order is `ordered`, and nothing else re-sorts it.
      fetched.forEach((bytes, offset) => {
        parts[start + offset] = bytes;
      });
    }

    // Carry the download count FORWARD across the rebuild. Rotation deletes
    // the asset that held it, and a counter that silently resets every time
    // someone presses Update answers a different question each week — the one
    // a reviewer is asking is whether this request's merged document has been
    // handed out, not whether this particular build has.
    const previous = yield* call(
      serviceLatestCombinedAsset,
      projectTdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );

    const version = yield* call(
      serviceNextCombinedVersion,
      projectTdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    // SAY WHICH NUMBER THIS BUILD GOT. The banner over the rebuild was
    // deriving its own — one past the newest combined asset — which is right
    // until those assets are gone: delete the merged PDF and rebuild, and it
    // announced version 1 over a build correctly numbered 5. The floor that
    // survives a delete is `latestCombinedVersion` on the project TDO, which
    // only this lookup can see.
    yield* put(COMBINE_PROJECT_VERSION({ projectTdoId, version }));
    // The project's own name is the base, so a rename carries through to what
    // lands on disk — a reviewer who calls the document something meaningful
    // should not find the request's name on the download.
    const baseName = previous.projectName || requestName;
    /**
     * `[NO_REDACTIONS]` only when EVERY member says it removed nothing AND a
     * reviewer stood behind each of those answers.
     *
     * The tag is a claim about the release, made to whoever receives it, and
     * `buildMergedDocumentFilename`'s own note is that a claim nobody can
     * check must not be made. An unreviewed member (VE-32440) has an empty
     * output for the opposite reason to a reviewed-and-clean one — nobody
     * read it — so counting it toward the claim is how a merged PDF of
     * documents nobody opened came to be handed over named as reviewed.
     *
     * `assertsNothingToRedact` is the shared form of that test — the strict
     * one, where a measurement does not count and silence does not either.
     */
    const hasRedactions = !ordered.every((output) =>
      assertsNothingToRedact(output)
    );
    const filename = buildMergedDocumentFilename(baseName, {
      ...previous.filenameOptions,
      hasRedactions,
      version,
    });

    // The buffers are TRANSFERRED here; nothing may touch `parts` afterwards.
    const combined = yield* call(combinePdfs, parts);
    const pageCounts = combined.partPageCounts ?? [];

    // Page ranges are computed from what the WORKER counted, not from each
    // part's recorded `numPages`: the record is what the per-file export said
    // at the time, and the ranges have to describe the bytes actually written.
    let cursor = 0;
    const memberDetails: CombinedDocumentDetails['members'] = ordered.map(
      (output, index) => {
        const pageCount = pageCounts[index] ?? 0;
        const startPage = cursor + 1;
        cursor += pageCount;
        return {
          tdoId: output.tdoId,
          name: output.name,
          assetId: output.assetId,
          version: output.version,
          pageCount,
          startPage,
          endPage: cursor,
          // Only when true, so every existing asset keeps its meaning.
          ...(output.includedUnreviewed ? { includedUnreviewed: true } : {}),
        };
      }
    );

    const details: CombinedDocumentDetails = {
      version,
      kind: 'document',
      projectTdoId,
      generatedDateTime: new Date().toISOString(),
      filename,
      hasRedactions,
      pageCount: combined.numPages,
      metrics: { downloads: previous.asset?.downloads ?? 0 },
      // The NAME comes from the caller, which has the request's files; the
      // combine only ever saw ids. Recorded so a released document can be
      // read back later and say what it does not contain.
      excluded: excludedMembers.map((tdoId) => ({
        tdoId,
        name: excludableByTdo.get(String(tdoId)) ?? null,
        reason: 'notWorkedOn' as const,
      })),
      members: memberDetails,
    };

    const saved = yield* call(
      serviceSaveCombinedDocumentAsset,
      projectTdoId,
      new Uint8Array(combined.pdfBytes),
      filename,
      details,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );

    // Mirrored AFTER the asset exists. The other order would burn a version
    // number on a combine that then failed, so the next successful one would
    // skip a number — harmless, but it makes the sequence stop meaning
    // "how many merged documents this project has issued".
    yield* call(
      serviceUpdateLatestCombinedVersion,
      projectTdoId,
      version,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );

    /**
     * ROTATE. Every combine leaves a new asset behind, and without this a
     * request accumulates one released PDF per press — several of them
     * downloadable, none of them marked, and "which one did we send?" becomes
     * a real question months later.
     */
    try {
      const existing = yield* call(
        serviceListCombinedAssetIds,
        projectTdoId,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      for (const id of existing.filter((assetId) => assetId !== saved.id)) {
        try {
          yield* call(
            serviceDeleteAsset,
            id,
            token,
            cfg.apiRoot,
            cfg.graphQLEndpoint,
            cfg.veritoneAppId
          );
        } catch (e) {
          console.warn(`Older combined asset delete failed for ${id}:`, e);
        }
      }
    } catch (e) {
      console.warn('Combined asset rotation skipped:', e);
    }

    /**
     * THE MERGED AUDIT LOG, and the release event that belongs in it.
     *
     * ALL OF IT INSIDE THIS `try`, including building the release row. The
     * enclosing handler's catch dispatches COMBINE_PROJECT_FAILURE and "Could
     * not create the merged document" — and by this line the PDF is built,
     * saved, version-mirrored and the previous build's assets are rotated
     * away. An earlier cut guarded the emit itself but left
     * `select(auditUserName)` outside, one line above it: the same unguarded
     * glc-redux slice read, in the same failure domain, reporting a failure
     * over a release that exists and is downloadable. A throw anywhere in here
     * must cost the LOG, not the release.
     *
     * The project is gathered FIRST, because it carries the set-wide acts —
     * the stamping and watermarking every member inherited, the membership
     * changes that decided what went in, and the release itself.
     */
    try {
      /**
       * THE RELEASE ROW is emitted for durability AND handed to the log
       * directly, rather than read back out of the platform:
       * `buildMergedAuditLogCsv`'s `extraRecords` carries the why. The
       * timestamp is pinned so the emitted row and the local one are the same
       * row, and a platform fast enough to return it does not produce it
       * twice.
       *
       * BY ID, like everything else. Naming the shipped members here was the
       * second attempt's one remaining exception, and it did not hold: every
       * later build re-reads this event out of the project's history, so a
       * document named by v1 is published by v2, v3 and every release after —
       * including one it was withheld from. The names are not lost for the
       * release being shipped: each member is its own gather source, so its
       * filename is in the CSV's SOURCE FILE column.
       */
      const releasedBy = yield* select(auditUserName);
      const releasedAt = new Date().toISOString();
      const releaseAction = `Built merged document (v${version})`;
      const releaseDetails = [
        `"${clip(filename)}"`,
        `${memberDetails.length} document(s), ${combined.numPages} page(s)`,
        describeMemberIds(memberDetails.map((member) => member.tdoId)),
        // What the release does NOT contain is part of what it is: a member
        // nobody opened is dropped rather than refused, and the trail has to
        // say which.
        ...(excludedMembers.length
          ? [`excluded ${describeMemberIds(excludedMembers)}`]
          : []),
      ].join('; ');
      yield* call(
        emitProjectAuditEvent,
        projectTdoId,
        releaseAction,
        releaseDetails,
        { timestamp: releasedAt }
      );
      const releaseRecord: SourcedAuditEventRecord = {
        payload: {
          action: releaseAction,
          timestamp: releasedAt,
          userName: releasedBy,
          details: releaseDetails,
        },
        sourceName: MERGED_DOCUMENT_SOURCE_NAME,
        sourceTdoId: String(projectTdoId),
      };
      const auditCsv = yield* call(
        buildMergedAuditLogCsv,
        [
          { tdoId: String(projectTdoId), name: MERGED_DOCUMENT_SOURCE_NAME },
          ...memberDetails.map((member) => ({
            tdoId: String(member.tdoId),
            name: member.name ?? '',
          })),
        ],
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId,
        [releaseRecord]
      );
      if (auditCsv) {
        const auditFilename = `${filename.replace(/\.pdf$/i, '')}.csv`;
        const savedLog = yield* call(
          serviceSaveMergedAuditLogAsset,
          projectTdoId,
          auditCsv,
          auditFilename,
          saved.id,
          version,
          token,
          cfg.apiRoot,
          cfg.graphQLEndpoint,
          cfg.veritoneAppId
        );
        // Rotate it on the same terms as the PDF: one current log, so
        // "which log goes with the document we sent" has one answer.
        if (savedLog) {
          const olderLogs = yield* call(
            serviceListMergedAuditLogAssets,
            projectTdoId,
            token,
            cfg.apiRoot,
            cfg.graphQLEndpoint,
            cfg.veritoneAppId
          );
          for (const { id } of olderLogs.filter(
            (log) => log.id !== savedLog.id
          )) {
            try {
              yield* call(
                serviceDeleteAsset,
                id,
                token,
                cfg.apiRoot,
                cfg.graphQLEndpoint,
                cfg.veritoneAppId
              );
            } catch (e) {
              console.warn(`Older merged audit log delete failed (${id}):`, e);
            }
          }
        }
      }
    } catch (e) {
      console.warn('Merged audit log skipped:', e);
    }

    yield* put(
      COMBINE_PROJECT_SUCCESS({
        projectTdoId,
        assetId: saved.id,
        version,
        filename,
      })
    );
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentReady',
          defaultMessage: 'Merged document created.',
        }),
        variant: 'success',
      })
    );
  } catch (err) {
    // The services throw with a reason; the console keeps it, the reviewer
    // gets a sentence. Same split the burn-in uses.
    console.error('[redact] merged document failed:', err);
    // ITS OWN `sagaIntl()`, because the one in the try may be exactly what
    // threw — and `fail` puts the terminal action before the message, so a
    // second throw here still leaves the waiting run ended.
    yield* fail(
      sagaIntl().formatMessage({
        id: 'mergedDocumentFailed',
        defaultMessage: 'Could not create the merged document.',
      })
    );
  }
}

/**
 * Read the project's newest merged document into the slice.
 */
function* handleLoadCombined(action: ReturnType<typeof LOAD_COMBINED_REQUEST>) {
  const { projectTdoId } = action.payload;
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  try {
    const latest = yield* call(
      serviceLatestCombinedAsset,
      projectTdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    yield* put(
      LOAD_COMBINED_SUCCESS({
        projectTdoId,
        asset: latest.asset,
        projectName: latest.projectName,
        filenameOptions: latest.filenameOptions,
        batesStamp: latest.batesStamp,
      })
    );
  } catch (err) {
    console.warn('[redact] merged document lookup failed', err);
    yield* put(LOAD_COMBINED_FAILURE({ projectTdoId }));
  }
}

/** After a combine, re-read it so the editor can offer what was just built. */
function* reloadAfterCombine(
  action: ReturnType<typeof COMBINE_PROJECT_SUCCESS>
) {
  const { projectTdoId } = action.payload;
  yield* put(LOAD_COMBINED_REQUEST({ projectTdoId }));
  /**
   * SHOW THE THING THAT WAS JUST MADE.
   */
  yield* put(SET_TAB({ tab: 'redacted-file' }));
  yield* put(SET_COMBINED_PREVIEW({ projectTdoId }));
}

/**
 * Withdraw a combined document.
 */
function* handleDeleteCombined(
  action: ReturnType<typeof DELETE_COMBINED_REQUEST>
) {
  const { projectTdoId, assetId } = action.payload;
  const intl = sagaIntl();

  // VE-31538. The last of the six set-wide writes to get a gate, and the
  // most destructive of them: this is the built merged document, the file
  // the agency releases. The disabled Delete button in `RedactedFilePreview`
  // is computed from a verdict that refreshes on the 60-second heartbeat, so
  // for up to a minute after a colleague takes the set it still looks live.
  if (!(yield* ensureSetUnlocked(projectTdoId))) {
    yield* put(
      DELETE_COMBINED_FAILURE({ projectTdoId, message: 'Set is in use' })
    );
    return;
  }

  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  try {
    yield* call(
      serviceDeleteAsset,
      assetId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );

    /**
     * THE AUDIT LOG GOES WITH IT.
     *
     * The build writes two assets — the merged PDF and the CSV that says how
     * it was redacted — and rotates them together so that "which log goes
     * with the document we sent" has one answer. Deleting only the PDF left
     * that question answered by a log describing a document that no longer
     * exists, and left the next build rotating against it.
     *
     * Best-effort and AFTER the PDF: the delete the reviewer asked for has
     * already succeeded, and failing the whole action over the companion
     * file would report a failure that did not happen. Logged, not raised,
     * and per id — the same terms the rotation above uses.
     *
     * The act itself is recorded independently: VE-31977 emits
     * "Withdrew merged document output" on DELETE_COMBINED_SUCCESS, so
     * removing the CSV is cleanup rather than erasure.
     */
    try {
      const logs = yield* call(
        serviceListMergedAuditLogAssets,
        projectTdoId,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      // ONLY THE LOG FOR THE ASSET BEING DELETED. The save records
      // `combinedAssetId` and the per-file path has always filtered on its
      // equivalent; this listed bare ids and so deleted every merged log on
      // the project. A rebuild landing between the gate and this loop would
      // have lost its brand-new log, leaving a live merged PDF with no
      // audit trail — the one outcome this cascade exists to prevent.
      const linked = logs.filter(
        (log) => log.details?.combinedAssetId === assetId
      );
      for (const { id } of linked) {
        // PER ID, as the rotation above does. One failure must not strand
        // the rest.
        try {
          yield* call(
            serviceDeleteAsset,
            id,
            token,
            cfg.apiRoot,
            cfg.graphQLEndpoint,
            cfg.veritoneAppId
          );
        } catch (e) {
          console.warn(`Merged audit log delete failed (${id}):`, e);
        }
      }
    } catch (e) {
      console.warn('Merged audit log listing skipped:', e);
    }

    yield* put(DELETE_COMBINED_SUCCESS({ projectTdoId }));
    yield* put(LOAD_COMBINED_REQUEST({ projectTdoId }));
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentDeleted',
          defaultMessage: 'Merged document deleted.',
        }),
      })
    );
  } catch (e: unknown) {
    const message = e instanceof Error ? e.message : 'Delete failed';
    yield* put(DELETE_COMBINED_FAILURE({ projectTdoId, message }));
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentDeleteFailed',
          defaultMessage: 'Could not delete the merged document.',
        }),
        variant: 'error',
      })
    );
  }
}

/**
 * Persist the bumped download count on the combined asset.
 */
function* handleIncrementCombinedDownload(
  action: ReturnType<typeof INCREMENT_COMBINED_DOWNLOAD>
) {
  const { projectTdoId } = action.payload;
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  try {
    const current = yield* call(
      serviceCombinedAssetDetails,
      projectTdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    if (!current) {
      return;
    }
    yield* call(
      serviceUpdateCombinedAssetDetails,
      current.assetId,
      {
        ...current.details,
        metrics: { downloads: (current.details.metrics?.downloads ?? 0) + 1 },
      },
      undefined,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    yield* put(LOAD_COMBINED_REQUEST({ projectTdoId }));
  } catch (e) {
    console.warn(
      'Failed to persist merged-document download count (non-fatal):',
      e
    );
  }
}

/**
 * CHOOSING A TAB ends the combined-document preview.
 */
function* clearPreviewOnTabChange(action: ReturnType<typeof SET_TAB>) {
  if (action.payload?.tab === 'redacted-file') {
    return;
  }
  const previewing = yield* select(selectPreviewingCombinedFor);
  if (previewing) {
    yield* put(SET_COMBINED_PREVIEW({ projectTdoId: null }));
  }
}

/**
 * Rename the merged document — or reset it, which is the same write with the
 * request's name.
 */
function* handleRenameProject(
  action: ReturnType<typeof RENAME_PROJECT_REQUEST>
) {
  const { projectTdoId, name, filenameOptions } = action.payload;
  const trimmed = name.trim();
  if (!trimmed) {
    return;
  }
  const intl = sagaIntl();
  // VE-31538. The name belongs to the set, not to the member on screen.
  // Ahead of the `renamedFrom` read below: a refusal must not consume the
  // rollback slot that the audit entry depends on.
  if (!(yield* ensureSetUnlocked(projectTdoId))) {
    yield* put(RENAME_PROJECT_FAILURE({ projectTdoId }));
    return;
  }
  // THE LIVE NAME IS NOT CAPTURED HERE, and cannot be. It used to be —
  // `select` for the name and the options, carried on the failure — and it
  // could not work: redux-saga runs reducers before putting an action on its
  // channel, so by this line the reducer has already written the NEW name and
  // a select reads that. The reducer stashes the pre-write pair itself; see
  // `renameRollback`.
  //
  // THAT SLOT is what the read below takes, and the only way the audit entry
  // at the end of this handler can say what the document used to be called
  // (VE-31977). It is read here rather than there because the success the
  // entry follows is what clears the slot.
  const renamedFrom = yield* select(selectRenamedFrom(projectTdoId));
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  try {
    yield* call(
      serviceRenameProjectTdo,
      projectTdoId,
      trimmed,
      filenameOptions,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    /**
     * SHOW WHAT WAS ASKED FOR, and do not re-read.
     */
    /**
     * RENAME THE ARTIFACT TOO, if one exists.
     */
    const current = yield* call(
      serviceCombinedAssetDetails,
      projectTdoId,
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint,
      cfg.veritoneAppId
    );
    if (current) {
      const issued = new Date(current.details.generatedDateTime);
      const filename = buildMergedDocumentFilename(
        trimmed,
        {
          appendMerged: filenameOptions.appendMerged,
          includeDate: filenameOptions.includeDate,
          // From the build's own record, not re-derived: the members were read
          // when it was made, and this is a fact about those bytes.
          hasRedactions: current.details.hasRedactions !== false,
          version: current.details.version,
        },
        Number.isNaN(issued.getTime()) ? new Date() : issued
      );
      yield* call(
        serviceUpdateCombinedAssetDetails,
        current.assetId,
        { ...current.details, filename },
        filename,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      /**
       * RE-READ THE ASSET, BUT NOT THE NAME.
       */
      const refreshed = yield* call(
        serviceLatestCombinedAsset,
        projectTdoId,
        token,
        cfg.apiRoot,
        cfg.graphQLEndpoint,
        cfg.veritoneAppId
      );
      yield* put(
        LOAD_COMBINED_SUCCESS({
          projectTdoId,
          asset: refreshed.asset,
          projectName: trimmed,
          // The options we just WROTE, not the ones that came back: the
          // re-read is served by the same cache the write does not clear.
          filenameOptions,
          // A rename does not touch Bates, so whatever the re-read returned is
          // current. Passed rather than omitted: an absent key means "not
          // looked up yet" to the reducer.
          batesStamp: refreshed.batesStamp,
        })
      );
    }
    yield* put(
      RENAME_PROJECT_SUCCESS({ projectTdoId, name: trimmed, filenameOptions })
    );
    // AFTER the success, but read BEFORE it: the rollback slot is cleared by
    // the reducer that handles RENAME_PROJECT_SUCCESS, so `renamedFrom` is
    // captured at the top of this handler.
    yield* call(
      emitProjectAuditEvent,
      projectTdoId,
      'Renamed merged document',
      renamedFrom && renamedFrom !== trimmed
        ? `"${renamedFrom}" → "${trimmed}"`
        : `"${trimmed}"`
    );
  } catch (e) {
    // Logged with the name that was attempted: a rename the gateway declines
    // looks identical on screen to one that never fired, and the console is
    // the only place the two can be told apart.
    console.warn('[redact] merged document rename failed', {
      projectTdoId,
      name: trimmed,
      error: e,
    });
    yield* put(
      enqueueSnackbar({
        message: intl.formatMessage({
          id: 'mergedDocumentRenameFailed',
          defaultMessage: 'Could not rename the merged document.',
        }),
        variant: 'error',
      })
    );
    // PUT THE NAME BACK. The optimistic write is what makes renaming feel
    // immediate; without this, a failure would leave a name on screen that is
    // not the document's, under a snackbar nobody keeps on screen either.
    yield* put(RENAME_PROJECT_FAILURE({ projectTdoId }));
    yield* put(LOAD_COMBINED_REQUEST({ projectTdoId }));
  }
}

export function* combineProjectSaga() {
  yield* takeLeading(COMBINE_PROJECT_REQUEST, handleCombineProject);
  yield* takeLatest(LOAD_COMBINED_REQUEST, handleLoadCombined);
  yield* takeEvery(COMBINE_PROJECT_SUCCESS, reloadAfterCombine);
  yield* takeEvery(DELETE_COMBINED_REQUEST, handleDeleteCombined);
  yield* takeEvery(
    INCREMENT_COMBINED_DOWNLOAD,
    handleIncrementCombinedDownload
  );
  yield* takeEvery(SET_TAB, clearPreviewOnTabChange);
  yield* takeEvery(RENAME_PROJECT_REQUEST, handleRenameProject);
}

export default combineProjectSaga;

/**
 * Store a merged document's Bates settings on the project TDO.
 *
 * `takeLeading`: Done closes the panel, so a second press cannot arrive from
 * the same surface, and the reducer has already shown the new stamp
 * optimistically. A failure puts the previous record back.
 */
/**
 * Write the Bates half of the merged set's redaction record.
 *
 * READ-MODIFY-WRITE, because the body is written whole and the watermarks
 * live in it too. That is the cost of the two concerns sharing one asset, and
 * the reason both writers run on one queue — see `watchProjectRedactionWrites`.
 * Spreading the record forward is what carries `watermarks`, `watermarksOff`
 * and `watermarksModifiedDateTime` through untouched, so numbering an already
 * marked release cannot stale every member's marks.
 *
 * REFUSES unless the record has been READ — but `none` COUNTS AS READ. It is
 * the answer "this set carries no asset yet", which is where every project
 * starts, so the first numbering applied to a set nobody had watermarked
 * composes onto `EMPTY_PROJECT_REDACTION_DATA`. Only `unread` and `failed`
 * refuse: there the body may hold marks this write would spread an empty
 * record over and silently drop.
 *
 * AND THE NO-OP TEST LIVES HERE, against the body as STORED. It cannot live
 * in the handler: the reducer applies optimistically and redux-saga calls
 * `next(action)` before the action reaches the channel, so by the time a
 * handler selects the stamp it is reading its OWN settings back and every
 * commit compares equal to itself. That is not a near-miss — it made the
 * write unreachable in every case while the panel reported success. The
 * stored body is the only copy the optimistic write has not already touched.
 *
 * Returns the stamp now IN FORCE: the one just written, or the stored one
 * when there was nothing to write. The caller records that rather than the
 * instant it minted, so the slice cannot claim a stamp the body does not
 * carry.
 */
function* writeProjectBatesIntoRedactionAsset(
  projectTdoId: TDOId,
  bates: BatesStampSettings | null,
  batesModifiedDateTime: string | null
) {
  const current = yield* select(selectProjectRedaction(projectTdoId));
  if (current.status === 'unread' || current.status === 'failed') {
    throw new Error(
      'merged document numbering: redaction record not read before write'
    );
  }
  const base =
    current.status === 'loaded' ? current.data : EMPTY_PROJECT_REDACTION_DATA;
  if (isSameBatesSettings(base.bates, bates)) {
    // Nothing changed, so nothing is minted. Reopening the panel and pressing
    // Done would otherwise stamp a fresh instant and send every member of the
    // release for a rebuild producing byte-identical PDFs.
    return base.batesModifiedDateTime;
  }
  const token: string = yield* select(selectToken);
  const cfg = yield* select(selectGraphQLConfig);
  const sending: ProjectRedactionData = {
    ...base,
    bates,
    batesModifiedDateTime,
  };
  const written = yield* call(() =>
    serviceWriteProjectRedactionData(
      projectTdoId,
      { ...sending, projectTdoId: String(projectTdoId) },
      token,
      cfg.apiRoot,
      cfg.graphQLEndpoint
    )
  );
  // The record on screen now describes what was stored, including the asset
  // it came from. Without this the next Bates write would read-modify-write
  // the PREVIOUS body and lose this one.
  yield* put(
    LOAD_PROJECT_REDACTION_SUCCESS({
      projectTdoId,
      data: { ...sending, assetId: written.id },
    })
  );
  return batesModifiedDateTime;
}

export function* handleSetProjectBates(
  action: ReturnType<typeof SET_PROJECT_BATES_REQUEST>
) {
  const { projectTdoId, settings } = action.payload;
  // CLAMPED BEFORE IT IS STORED, through the same bounds the reader applies.
  // Without this the optimistic value could differ from what reads back — a
  // 25-character prefix previewing in full and silently truncating to 20 on
  // the next load.
  const clamped = clampBatesSettings(settings);
  // The reducer ran first (redux-saga calls `next(action)` before putting the
  // action on its channel), so this is the stamp it just assigned.
  //
  // THE UNCHANGED-COMMIT TEST IS NOT MADE HERE, deliberately. This selector
  // reads what the reducer has already applied, so comparing it against the
  // settings that produced it compares them with themselves and is true for
  // every commit — which is how this write came to do nothing at all. The
  // test is made against the stored body, inside the writer.
  const applied = yield* select(selectProjectBatesStamp(projectTdoId));
  const minted = applied?.modifiedDateTime ?? new Date().toISOString();
  try {
    // The stamp IN FORCE once this returns, which is the minted one on a real
    // change and the stored one when there was nothing to write. Recording
    // what was minted regardless would leave the slice claiming a stamp the
    // body does not carry, and every member reading behind for ever.
    const inForce = yield* call(
      writeProjectBatesIntoRedactionAsset,
      projectTdoId,
      clamped,
      minted
    );
    const record: ProjectBatesStamp = {
      modifiedDateTime: inForce,
      settings: clamped,
    };
    yield* put(SET_PROJECT_BATES_SUCCESS({ projectTdoId, batesStamp: record }));
  } catch (err) {
    // The message, not the error: it interpolates the server's GraphQL text,
    // which can echo the prefix the operator typed — often a case number.
    console.warn(
      '[redact] merged document Bates write failed',
      err instanceof Error ? err.name : 'unknown error'
    );
    // Rolled back from the slot the REDUCER stashed. Nothing is reconstructed
    // here: by the time this runs the previous record is gone from the slice.
    yield* put(SET_PROJECT_BATES_FAILURE({ projectTdoId }));
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage({
          id: 'mergedDocumentBatesFailed',
          defaultMessage:
            'Could not apply Bates stamping to the merged document.',
        }),
        variant: 'error',
      })
    );
  }
}

/** Switch a merged document's numbering off. Members go stale. */
export function* handleClearProjectBates(
  action: ReturnType<typeof CLEAR_PROJECT_BATES_REQUEST>
) {
  const { projectTdoId } = action.payload;
  try {
    // Null settings AND a null stamp: the set numbers nothing, and an output
    // still carrying a stamp is `strayNumbering` rather than merely behind.
    yield* call(writeProjectBatesIntoRedactionAsset, projectTdoId, null, null);
    yield* put(CLEAR_PROJECT_BATES_SUCCESS({ projectTdoId }));
  } catch (err) {
    console.warn(
      '[redact] merged document Bates clear failed',
      err instanceof Error ? err.name : 'unknown error'
    );
    yield* put(SET_PROJECT_BATES_FAILURE({ projectTdoId }));
    yield* put(
      enqueueSnackbar({
        message: sagaIntl().formatMessage({
          id: 'mergedDocumentBatesFailed',
          defaultMessage:
            'Could not apply Bates stamping to the merged document.',
        }),
        variant: 'error',
      })
    );
  }
}
